(() => {
  const catalog = window.C2G_TRAILER_FITMENT_CATALOG;
  if (!catalog?.products?.length) return;

  const normalize = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9.]/g, '');
  const unique = (values) => [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' });
  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
  const numberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const canonicalBolt = (value) => {
    const normalized = normalize(value);
    const aliases = {
      '5X45': '5X114.3',
      '5ON45': '5X114.3',
      '6X55': '6X139.7',
      '6ON55': '6X139.7',
      '8X65': '8X165.1',
      '8ON65': '8X165.1'
    };
    return aliases[normalized] || normalized;
  };

  const currentKind = (finder) => finder.querySelector('[data-c2g-trailer-kind][aria-selected="true"]')?.dataset.c2gTrailerKind || 'tire';
  const productsFor = (kind) => catalog.products.filter((product) => product.kind === kind);
  const field = (finder, name) => finder.querySelector(`[data-c2g-field="${name}"]`);
  const fieldValue = (finder, name) => field(finder, name)?.value?.trim() || '';

  const fillSelect = (element, values) => {
    if (!element) return;
    const selected = element.value;
    const placeholder = element.dataset.placeholder || '';
    element.replaceChildren(new Option(placeholder, ''));
    for (const value of unique(values)) element.add(new Option(value, value));
    if ([...element.options].some((option) => option.value === selected)) element.value = selected;
  };

  const choicesFor = (products, key) => products.map((product) => product.specs[key]);
  const initializeChoices = (finder) => {
    fillSelect(field(finder, 'tireSize'), choicesFor(productsFor('tire'), 'tireSize'));
    fillSelect(field(finder, 'loadRange'), choicesFor(productsFor('tire'), 'loadRange'));
    fillSelect(field(finder, 'wheelSize'), choicesFor(productsFor('wheel'), 'wheelSize'));
    fillSelect(field(finder, 'boltPattern'), choicesFor(productsFor('wheel'), 'boltPattern'));
    fillSelect(field(finder, 'assemblyTireSize'), choicesFor(productsFor('assembly'), 'tireSize'));
    fillSelect(field(finder, 'assemblyWheelSize'), choicesFor(productsFor('assembly'), 'wheelSize'));
    fillSelect(field(finder, 'assemblyBoltPattern'), choicesFor(productsFor('assembly'), 'boltPattern'));
    fillSelect(field(finder, 'assemblyLoadRange'), choicesFor(productsFor('assembly'), 'loadRange'));
  };

  const setKind = (finder, kind, focusTab = false) => {
    finder.querySelectorAll('[data-c2g-trailer-kind]').forEach((tab) => {
      const selected = tab.dataset.c2gTrailerKind === kind;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focusTab) tab.focus();
    });
    finder.querySelectorAll('[data-c2g-trailer-panel]').forEach((panel) => {
      const selected = panel.dataset.c2gTrailerPanel === kind;
      panel.hidden = !selected;
      panel.querySelectorAll('input, select').forEach((control) => {
        control.disabled = !selected;
      });
    });
    const kindInput = finder.querySelector('[data-c2g-fit-kind-input]');
    if (kindInput) kindInput.value = kind;
  };

  const exact = (left, right) => normalize(left) === normalize(right);
  const fitProducts = (finder, kind) => {
    const requirements = {
      tireSize: kind === 'tire' ? fieldValue(finder, 'tireSize') : kind === 'assembly' ? fieldValue(finder, 'assemblyTireSize') : '',
      loadRange: kind === 'tire' ? fieldValue(finder, 'loadRange') : kind === 'assembly' ? fieldValue(finder, 'assemblyLoadRange') : '',
      wheelSize: kind === 'wheel' ? fieldValue(finder, 'wheelSize') : kind === 'assembly' ? fieldValue(finder, 'assemblyWheelSize') : '',
      boltPattern: kind === 'wheel' ? fieldValue(finder, 'boltPattern') : kind === 'assembly' ? fieldValue(finder, 'assemblyBoltPattern') : '',
      minTireLoad: kind === 'tire' ? numberValue(fieldValue(finder, 'minTireLoad')) : null,
      minWheelLoad: kind === 'wheel' ? numberValue(fieldValue(finder, 'minWheelLoad')) : null,
      centerBore: kind === 'wheel' ? numberValue(fieldValue(finder, 'centerBore')) : null
    };

    return productsFor(kind).filter((product) => {
      const specs = product.specs;
      if (requirements.tireSize && !exact(specs.tireSize, requirements.tireSize)) return false;
      if (requirements.loadRange && !exact(specs.loadRange, requirements.loadRange)) return false;
      if (requirements.wheelSize && !exact(specs.wheelSize, requirements.wheelSize)) return false;
      if (requirements.boltPattern && canonicalBolt(specs.boltPattern) !== canonicalBolt(requirements.boltPattern)) return false;
      if (requirements.minTireLoad && specs.maxLoadLbs !== null && specs.maxLoadLbs < requirements.minTireLoad) return false;
      if (requirements.minWheelLoad && specs.wheelLoadLbs !== null && specs.wheelLoadLbs < requirements.minWheelLoad) return false;
      if (requirements.centerBore && specs.centerBoreMm !== null && specs.centerBoreMm < requirements.centerBore) return false;
      return true;
    }).map((product) => {
      const unknowns = [];
      if (requirements.minTireLoad && product.specs.maxLoadLbs === null) unknowns.push('maxLoadLbs');
      if (requirements.minWheelLoad && product.specs.wheelLoadLbs === null) unknowns.push('wheelLoadLbs');
      if (requirements.centerBore && product.specs.centerBoreMm === null) unknowns.push('centerBoreMm');
      if (kind === 'wheel' && !product.specs.wheelSize) unknowns.push('wheelSize');
      if (kind === 'wheel' && !product.specs.boltPattern) unknowns.push('boltPattern');
      if (kind === 'wheel' && product.specs.centerBoreMm === null) unknowns.push('centerBoreMm');
      if (kind === 'wheel' && product.specs.wheelLoadLbs === null) unknowns.push('wheelLoadLbs');
      if (kind === 'assembly' && !product.specs.wheelSize) unknowns.push('wheelSize');
      if (kind === 'assembly' && !product.specs.boltPattern) unknowns.push('boltPattern');
      if (kind === 'assembly' && product.specs.centerBoreMm === null) unknowns.push('centerBoreMm');
      if (kind === 'assembly' && product.specs.maxLoadLbs === null) unknowns.push('maxLoadLbs');
      return { ...product, unknowns: [...new Set(unknowns)] };
    }).sort((left, right) => Number(right.snapshot.available) - Number(left.snapshot.available) || left.unknowns.length - right.unknowns.length || left.title.localeCompare(right.title));
  };

  const parameterString = (finder, kind) => {
    const values = new URLSearchParams();
    values.set('view', 'trailer-fitment');
    values.set('fit_kind', kind);
    finder.querySelectorAll(`[data-c2g-trailer-panel="${kind}"] input, [data-c2g-trailer-panel="${kind}"] select`).forEach((control) => {
      if (control.name && control.value.trim()) values.set(control.name, control.value.trim());
    });
    return values.toString();
  };

  const prefill = (finder) => {
    const params = new URLSearchParams(window.location.search);
    const kind = ['tire', 'wheel', 'assembly'].includes(params.get('fit_kind')) ? params.get('fit_kind') : 'tire';
    setKind(finder, kind);
    finder.querySelectorAll('input[name], select[name]').forEach((control) => {
      const value = params.get(control.name);
      if (value !== null && [...(control.options || [])].some((option) => option.value === value)) control.value = value;
      else if (value !== null && control.tagName !== 'SELECT') control.value = value;
    });
    return params.has('fit_kind');
  };

  const prefillHelpRequest = () => {
    const params = new URLSearchParams(window.location.search);
    const kind = params.get('fit_kind');
    if (!['tire', 'wheel', 'assembly'].includes(kind)) return;
    const type = document.querySelector('#c2g-fitment-type');
    const specs = document.querySelector('#c2g-fitment-size');
    const details = document.querySelector('#c2g-fitment-details');
    const names = {
      tire: ['Trailer tires', ['tire_size', 'load_range', 'min_tire_load']],
      wheel: ['Wheels', ['wheel_size', 'bolt_pattern', 'min_wheel_load', 'center_bore']],
      assembly: ['Assemblies', ['assembly_tire_size', 'assembly_wheel_size', 'assembly_bolt_pattern', 'assembly_load_range']]
    };
    if (type) type.value = names[kind][0];
    const values = names[kind][1].map((name) => params.get(name)).filter(Boolean);
    if (specs && (!specs.value || specs.dataset.c2gFitmentPrefilled === 'true')) {
      specs.value = values.join(', ');
      specs.dataset.c2gFitmentPrefilled = 'true';
    }
    if (details && (!details.value || details.dataset.c2gFitmentPrefilled === 'true')) {
      details.value = `${document.querySelector('[data-c2g-trailer-finder]')?.dataset.labelRequestPrefill || 'Trailer fitment request'}: ${values.join(', ')}`;
      details.dataset.c2gFitmentPrefilled = 'true';
    }
  };

  const specItems = (product, finder) => {
    const labels = finder.dataset;
    const specs = product.specs;
    const values = product.kind === 'tire'
      ? [[labels.labelTireSize, specs.tireSize], [labels.labelLoadRange, specs.loadRange], [labels.labelMaxLoad, specs.maxLoadLbs ? `${specs.maxLoadLbs.toLocaleString()} lb` : '']]
      : product.kind === 'wheel'
        ? [[labels.labelWheelSize, specs.wheelSize], [labels.labelBoltPattern, specs.boltPattern], [labels.labelCenterBore, specs.centerBoreMm ? `${specs.centerBoreMm} mm` : ''], [labels.labelWheelLoad, specs.wheelLoadLbs ? `${specs.wheelLoadLbs.toLocaleString()} lb` : '']]
        : [[labels.labelTireSize, specs.tireSize], [labels.labelWheelSize, specs.wheelSize], [labels.labelBoltPattern, specs.boltPattern], [labels.labelLoadRange, specs.loadRange]];
    if (product.packQty > 1) values.push([labels.labelPackTitle, labels.labelPack.replace('__COUNT__', String(product.packQty))]);
    return values.filter(([, value]) => value).map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('');
  };

  const card = (product, finder) => {
    const verify = product.unknowns.length > 0;
    const price = product.snapshot.price === null ? '' : money.format(product.snapshot.price);
    const productUrl = `/products/${encodeURIComponent(product.handle)}`;
    const stockLabel = product.snapshot.available ? finder.dataset.labelInStock : finder.dataset.labelCheckAvailability;
    const displayTitle = product.title.replace(/[—–]/g, '-');
    return `<article class="c2g-fit-card" data-product-handle="${escapeHtml(product.handle)}">
      <div class="c2g-fit-card__media">
        ${product.image?.url ? `<img src="${escapeHtml(product.image.url)}" alt="${escapeHtml(product.image.alt || product.title)}" loading="lazy">` : ''}
        <span class="c2g-fit-card__badge${verify ? ' c2g-fit-card__badge--verify' : ''}">${escapeHtml(verify ? finder.dataset.labelVerify : finder.dataset.labelMatch)}</span>
      </div>
      <div class="c2g-fit-card__body">
        <h3>${escapeHtml(displayTitle)}</h3>
        <dl class="c2g-fit-card__specs">${specItems(product, finder)}</dl>
        <div class="c2g-fit-card__buy">
          <div>${price ? `<span class="c2g-fit-card__price">${escapeHtml(price)}</span>` : ''}<span class="c2g-fit-card__stock${product.snapshot.available ? '' : ' c2g-fit-card__stock--out'}">${escapeHtml(stockLabel)}</span></div>
          <a href="${productUrl}">${escapeHtml(finder.dataset.labelViewProduct)}</a>
        </div>
      </div>
    </article>`;
  };

  const refreshProductState = async (finder, products) => {
    const states = await Promise.allSettled(products.map(async (product) => {
      const response = await fetch(`/products/${encodeURIComponent(product.handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const live = await response.json();
      const cardElement = finder.querySelector(`[data-product-handle="${CSS.escape(product.handle)}"]`);
      if (!cardElement) return false;
      const priceElement = cardElement.querySelector('.c2g-fit-card__price');
      const stockElement = cardElement.querySelector('.c2g-fit-card__stock');
      if (priceElement && Number.isFinite(live.price)) priceElement.textContent = money.format(live.price / 100);
      if (stockElement) {
        stockElement.textContent = live.available ? finder.dataset.labelInStock : finder.dataset.labelOutOfStock;
        stockElement.classList.toggle('c2g-fit-card__stock--out', !live.available);
      }
      return true;
    }));
    return states.filter((state) => state.status !== 'fulfilled' || state.value !== true).length;
  };

  const renderResults = async (finder, scroll = true) => {
    const results = finder.querySelector('[data-c2g-trailer-results]');
    if (!results) return;
    const kind = currentKind(finder);
    const matches = fitProducts(finder, kind);
    const grid = results.querySelector('[data-c2g-results-grid]');
    const empty = results.querySelector('[data-c2g-results-empty]');
    const status = results.querySelector('[data-c2g-results-status]');
    results.hidden = false;
    grid.replaceChildren();
    empty.hidden = matches.length > 0;
    status.textContent = matches.length ? finder.dataset.labelLoading : finder.dataset.labelNoResultsText;
    if (matches.length) grid.innerHTML = matches.map((product) => card(product, finder)).join('');
    window.history.replaceState({}, '', `${window.location.pathname}?${parameterString(finder, kind)}#trailer-fitment-results`);
    prefillHelpRequest();
    results.id = 'trailer-fitment-results';
    if (scroll) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      results.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    if (matches.length) {
      const failedRefreshes = await refreshProductState(finder, matches);
      status.textContent = failedRefreshes
        ? finder.dataset.labelRefreshPartial.replace('__COUNT__', String(matches.length))
        : finder.dataset.labelResults.replace('__COUNT__', String(matches.length));
    }
  };

  const handleSubmit = (event) => {
    const form = event.target.closest('[data-c2g-trailer-form]');
    if (!form) return;
    event.preventDefault();
    const finder = form.closest('[data-c2g-trailer-finder]');
    if (!form.reportValidity()) return;
    const kind = currentKind(finder);
    if (finder.dataset.showResults === 'true') {
      renderResults(finder);
      return;
    }
    window.location.assign(`${finder.dataset.resultsUrl}?${parameterString(finder, kind)}`);
  };

  document.querySelectorAll('[data-c2g-trailer-finder]').forEach((finder) => {
    initializeChoices(finder);
    const hasParams = prefill(finder);
    if (hasParams && finder.dataset.showResults === 'true') renderResults(finder, false);
  });
  prefillHelpRequest();

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-c2g-trailer-kind]');
    if (!tab) return;
    setKind(tab.closest('[data-c2g-trailer-finder]'), tab.dataset.c2gTrailerKind);
  });
  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-c2g-trailer-kind]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const finder = tab.closest('[data-c2g-trailer-finder]');
    const tabs = [...finder.querySelectorAll('[data-c2g-trailer-kind]')];
    const index = tabs.indexOf(tab);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    setKind(finder, tabs[next].dataset.c2gTrailerKind, true);
  });
  document.addEventListener('submit', handleSubmit);
})();
